import { LoaderFunctionArgs, redirect, useLoaderData } from "react-router"
import { DocsRouterLink as RouterLink } from "~/docs-data/docs-link"
import { DatabaseIcon } from "lucide-react"
import { H1, H2, P, Section } from "~/components/Typography"
import { Badge } from "~/components/ui/badge"
import { LocationTypeBadge } from "~/components/LocationTypeBadge"
import { MarkdownBody } from "~/components/MarkdownBody"
import { metrics, locationSets } from "~/docs-data/concepts"
import { COUNTRY_PRESENTATION } from "~/docs-data/country-catalogs"
import {
  canonicalMetricSlug,
  labelWithCadence,
  ltmFamilyId,
  metricCanonicalName,
  parseCanonicalMetricSlug,
  relatedMetricConcepts,
} from "~/docs-data/helpers"
import { getDocsCatalogForRequest } from "~/server/docs-catalog-store.server"
export const loader = async ({ request, params }: LoaderFunctionArgs) => {
  const id = params.id ?? ""
  const docs = await getDocsCatalogForRequest(request)
  // Alias redirects below keep the query string so markers like
  // `?from-docs=` (legacy docs.unacast.com arrivals, CAT-1204) survive.
  const { search } = new URL(request.url)

  // Resolution order:
  //   1) Per-location-type canonical slug (`foot-traffic-place`,
  //      `trade-areas-area`, …) — overview page scoped to a single
  //      location type. Each location type gets its own page so the listing
  //      doesn't mix Place and Area variants.
  //   2) Backend LTM (`places_foot_traffic_month`, …) — single concrete
  //      metric page.
  //   3) Legacy LTM family (`places_foot_traffic`) — covers any old URLs
  //      that still encode the LTM-id family.
  const typedSlug = parseCanonicalMetricSlug(id)
  if (typedSlug) {
    const { baseSlug, locationType } = typedSlug
    const canonicalVariants = docs.locationTypeMetrics.filter(
      (x) =>
        x.locationTypeId === locationType &&
        canonicalMetricSlug(x.displayName) === baseSlug,
    )
    if (canonicalVariants.length === 1) {
      // No need for an overview when there's only one variant — redirect
      // straight to that LTM's concrete metric page.
      throw redirect(
        `/docs/concepts/metrics/${canonicalVariants[0].id}${search}`,
      )
    }
    if (canonicalVariants.length >= 1) {
      const variantIds = new Set(canonicalVariants.map((x) => x.id))
      const concept = metricCanonicalName(canonicalVariants[0].displayName)
      // Pull a matching hardcoded editorial concept if one exists — gives us
      // a rich intro for well-known metrics. Match on id (`foot-traffic`) or
      // on canonical name (case-insensitive), and only when the editorial
      // concept's location type agrees.
      const editorialConcept =
        metrics.find(
          (x) =>
            x.id === baseSlug &&
            (x.locationType as string) === locationType,
        ) ??
        metrics.find(
          (x) =>
            x.name.toLowerCase() === concept.toLowerCase() &&
            (x.locationType as string) === locationType,
        )
      return {
        kind: "canonical" as const,
        conceptName: concept,
        slug: id,
        locationType,
        variants: canonicalVariants,
        editorialConcept,
        datasetsForMetric: docs.datasets.filter(
          (d) => variantIds.has(d.ltm) && d.locationType === locationType,
        ),
        related: relatedMetricConcepts(
          docs.datasets,
          concept,
          locationType,
          editorialConcept?.description ?? "",
        ),
        hasAuthoredRelated: /##\s*Related metrics/i.test(
          editorialConcept?.description ?? "",
        ),
      }
    }
  }
  const ltm = docs.locationTypeMetrics.find((x) => x.id === id)
  if (ltm) {
    // Sibling cadence variants: same concept (canonical name) and location
    // type, different cadence. Surfaced so the LTM page can offer a switcher.
    const base = canonicalMetricSlug(ltm.displayName)
    const variants = docs.locationTypeMetrics.filter(
      (x) =>
        x.locationTypeId === ltm.locationTypeId &&
        canonicalMetricSlug(x.displayName) === base,
    )
    return {
      kind: "ltm" as const,
      ltm,
      variants,
      datasetsForMetric: docs.datasets.filter((d) => d.ltm === id),
      related: relatedMetricConcepts(
        docs.datasets,
        metricCanonicalName(ltm.displayName),
        ltm.locationTypeId,
        ltm.description ?? "",
      ),
      hasAuthoredRelated: /##\s*Related metrics/i.test(ltm.description ?? ""),
    }
  }
  const familyVariants = docs.locationTypeMetrics.filter(
    (x) => ltmFamilyId(x.id) === id,
  )
  if (familyVariants.length >= 2) {
    return {
      kind: "family" as const,
      familyId: id,
      variants: familyVariants,
    }
  }
  // 4) Bare editorial concept id (`foot-traffic`, `visitor-journey`) — the
  //    ids guides reference in `relatedMetrics`. Redirect to the
  //    per-location-type canonical page for the concept's location type.
  //    Ids that already carry a type suffix were handled (or 404'd) above.
  const editorial = metrics.find((m) => m.id === id)
  if (editorial && !id.endsWith(`-${editorial.locationType}`)) {
    const base = id === "foot-traffic-vwr" ? "foot-traffic" : id
    throw redirect(`/docs/concepts/metrics/${base}-${editorial.locationType}${search}`)
  }
  throw new Response("Metric not found", { status: 404 })
}

export default function MetricPage() {
  const data = useLoaderData<typeof loader>()
  if (data.kind === "canonical") return <CanonicalMetricView data={data} />
  if (data.kind === "family") return <FamilyMetricView data={data} />
  return <LtmMetricView data={data} />
}

const CADENCE_ORDER: Record<string, number> = {
  Hourly: 0,
  Daily: 1,
  Weekly: 2,
  Monthly: 3,
  Quarterly: 4,
  Yearly: 5,
}

// Shown once above the cadence list instead of a per-variant blurb. Explains
// that cadences share a concept but differ slightly in schema and fit.
const CADENCE_NOTE =
  "Available at several cadences. The schema differs slightly between them and " +
  "each suits different use cases. Longer cadences (e.g. monthly) often add " +
  "aggregate columns such as sums and averages that aren't meaningful at shorter " +
  "cadences like hourly or daily. Open a cadence for its full schema and datasets."

// Compact, name-only list of cadence variants: a cadence badge + the variant
// name (cadence word stripped, since the badge already carries it). Replaces
// the description-heavy card grid so the available cadences are easy to scan.
function CadenceVariantList({ variants }: { variants: any[] }) {
  const sorted = [...variants].sort((a, b) => {
    const aCad = a.inputs?.[0]?.cadence ?? ""
    const bCad = b.inputs?.[0]?.cadence ?? ""
    const cmp = (CADENCE_ORDER[aCad] ?? 99) - (CADENCE_ORDER[bCad] ?? 99)
    return cmp !== 0 ? cmp : a.id.localeCompare(b.id)
  })
  return (
    <div className="border rounded-md overflow-hidden divide-y">
      {sorted.map((v: any) => {
        const cadence = v.inputs?.[0]?.cadence ?? ""
        return (
          <RouterLink
            key={v.id}
            to={`/docs/concepts/metrics/${v.id}`}
            className="group flex items-center gap-3 px-4 py-2.5 hover:bg-muted transition-colors text-sm"
          >
            <Badge
              variant="secondary"
              className="text-xs w-20 justify-center shrink-0"
            >
              {cadence || "—"}
            </Badge>
            <span className="font-medium text-foreground group-hover:text-primary transition-colors">
              {labelWithCadence(v.displayName, "")}
            </span>
          </RouterLink>
        )
      })}
    </div>
  )
}

function FamilyMetricView({
  data,
}: {
  data: { kind: "family"; familyId: string; variants: any[] }
}) {
  const { variants } = data
  // Sort variants by cadence so Day → Week → Month reads naturally. The
  // family display name comes from the first variant with its cadence word
  // stripped (labelWithCadence("", "") strips the trailing cadence token).
  const sorted = [...variants].sort((a, b) => {
    const aCad = a.inputs?.[0]?.cadence ?? ""
    const bCad = b.inputs?.[0]?.cadence ?? ""
    return (CADENCE_ORDER[aCad] ?? 99) - (CADENCE_ORDER[bCad] ?? 99)
  })
  const first = sorted[0]
  const familyName = labelWithCadence(first.displayName, "")
  const locationType = first.locationTypeId as "place" | "area" | "way"

  return (
    <div className="max-w-screen-xl">
      <Section>
        <div className="flex items-center gap-2 mb-3">
          <LocationTypeBadge type={locationType} />
        </div>
        <H1>{familyName}</H1>
        <P className="text-base text-muted-foreground">
          {familyName} is available at several cadences. Pick the one that
          matches your analysis.
        </P>
      </Section>

      <Section>
        <H2>Available cadences</H2>
        <P className="text-sm text-muted-foreground">{CADENCE_NOTE}</P>
        <CadenceVariantList variants={sorted} />
      </Section>
    </div>
  )
}

// Overview page for a metric concept (e.g. "Foot Traffic"). Reached via the
// canonical-name slug — the nav points everyone here so clicks land on a
// shared concept page rather than dropping into a specific cadence. Lists
// every LTM whose displayName collapses to the same canonical name as cards;
// includes editorial intro from the hardcoded concept when one matches.
const LOCATION_TYPE_LABEL: Record<string, string> = {
  place: "Place",
  area: "Area",
  way: "Way",
}

function CanonicalMetricView({
  data,
}: {
  data: {
    kind: "canonical"
    conceptName: string
    slug: string
    locationType: "place" | "area" | "way"
    variants: any[]
    editorialConcept: any | undefined
    datasetsForMetric: any[]
    related: { name: string; to: string }[]
    hasAuthoredRelated: boolean
  }
}) {
  const {
    conceptName,
    locationType,
    variants,
    editorialConcept,
    datasetsForMetric,
    related,
    hasAuthoredRelated,
  } = data
  // Variants are already filtered to this location type by the loader.
  const sorted = [...variants].sort((a, b) => {
    const aCad = a.inputs?.[0]?.cadence ?? ""
    const bCad = b.inputs?.[0]?.cadence ?? ""
    const cmp = (CADENCE_ORDER[aCad] ?? 99) - (CADENCE_ORDER[bCad] ?? 99)
    return cmp !== 0 ? cmp : a.id.localeCompare(b.id)
  })
  const typeLabel = LOCATION_TYPE_LABEL[locationType] ?? locationType

  return (
    <div className="max-w-screen-xl">
      <Section>
        <div className="flex items-center gap-2 mb-3">
          <LocationTypeBadge type={locationType} />
        </div>
        <H1>{conceptName}</H1>
        {editorialConcept?.description ? (
          <P className="text-base text-muted-foreground">
            {editorialConcept.description}
          </P>
        ) : (
          <P className="text-base text-muted-foreground">
            {conceptName} on {typeLabel} is available at several cadences. Pick
            the one that matches your analysis.
          </P>
        )}
        <AvailableCountries datasets={datasetsForMetric} />
      </Section>

      <Section>
        <H2>Available cadences</H2>
        <P className="text-sm text-muted-foreground">{CADENCE_NOTE}</P>
        <CadenceVariantList variants={sorted} />
      </Section>

      <Section>
        <H2>Datasets using this metric</H2>
        {datasetsForMetric.length === 0 ? (
          <P className="text-muted-foreground italic">
            No materialized datasets yet.
          </P>
        ) : (
          <ul className="space-y-0.5">
            {datasetsForMetric.map((d: any) => {
              const editorial = locationSets.find((ls) => ls.id === d.locationSet)
              const mapName =
                d.locationSetName ?? editorial?.shortName ?? d.locationSet
              return (
                <li key={d.slug}>
                  <DatasetRowLink
                    to={`/docs/datasets/${d.country.toLowerCase()}/${d.slug}`}
                    country={d.country}
                    mapName={mapName}
                    metricName={labelWithCadence(d.name, "")}
                    cadence={d.cadence}
                  />
                </li>
              )
            })}
          </ul>
        )}
      </Section>

      <RelatedMetrics related={related} hasAuthoredRelated={hasAuthoredRelated} />
    </div>
  )
}

function LtmMetricView({
  data,
}: {
  data: {
    kind: "ltm"
    ltm: any
    variants?: any[]
    datasetsForMetric: any[]
    related: { name: string; to: string }[]
    hasAuthoredRelated: boolean
  }
}) {
  const { ltm, datasetsForMetric, related, hasAuthoredRelated } = data
  // Other cadence variants of the same concept, ordered, current one excluded.
  const otherVariants = [...(data.variants ?? [])]
    .sort((a, b) => {
      const aCad = a.inputs?.[0]?.cadence ?? ""
      const bCad = b.inputs?.[0]?.cadence ?? ""
      return (CADENCE_ORDER[aCad] ?? 99) - (CADENCE_ORDER[bCad] ?? 99)
    })
    .filter((v) => v.id !== ltm.id)
  return (
    <div className="max-w-screen-xl">
      <Section>
        <div className="flex items-center gap-2 mb-3">
          <LocationTypeBadge type={ltm.locationTypeId as "place" | "area" | "way"} />
          {ltm.inputs?.[0]?.cadence && (
            <Badge variant="secondary" className="text-xs">
              {ltm.inputs[0].cadence}
            </Badge>
          )}
        </div>
        <H1>{labelWithCadence(ltm.displayName, "")}</H1>
        {otherVariants.length > 0 && (
          <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
            <span className="text-muted-foreground">
              Also available at other cadences:
            </span>
            {otherVariants.map((v) => (
              <RouterLink
                key={v.id}
                to={`/docs/concepts/metrics/${v.id}`}
                className="rounded-full border px-2.5 py-0.5 text-xs font-medium text-muted-foreground transition-colors hover:border-primary hover:text-primary"
              >
                {v.inputs?.[0]?.cadence ?? labelWithCadence(v.displayName, "")}
              </RouterLink>
            ))}
          </div>
        )}
        {ltm.shortDescription && (
          <P className="mt-3 text-base text-muted-foreground">{ltm.shortDescription}</P>
        )}
        <AvailableCountries datasets={datasetsForMetric} />
      </Section>

      {ltm.description && (
        <Section>
          <MarkdownBody source={ltm.description} />
        </Section>
      )}

      {ltm.columns.length > 0 && (
        <Section>
          <H2>Schema</H2>
          <P className="text-sm text-muted-foreground">
            This is the <strong>standard schema</strong> for the metric.
            Always check the schema on the specific dataset you're using, since
            small variations may occur per country, map, or cadence. See the
            list of datasets using this metric below.
          </P>
          <P className="text-sm text-muted-foreground">
            <strong>Map columns</strong> (e.g. ZIP code, state, brand, polygon)
            are usually exposed on each dataset as <em>available columns that
            aren't included by default</em>. They're not listed here because
            each dataset is connected to a different map and therefore has a
            different set of map columns available. Open a specific dataset
            below to see its full schema.
          </P>
          <div className="border rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-left">
                <tr>
                  <th className="px-4 py-2 font-medium">Column</th>
                  <th className="px-4 py-2 font-medium">Type</th>
                  <th className="px-4 py-2 font-medium">Description</th>
                </tr>
              </thead>
              <tbody>
                {ltm.columns.map((c: any) => (
                  <tr key={c.name} className="border-t">
                    <td className="px-4 py-3 font-mono text-xs">{c.name}</td>
                    <td className="px-4 py-3 font-mono text-xs">{c.type}</td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {c.description || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      <Section>
        <H2>Datasets using this metric</H2>
        {datasetsForMetric.length === 0 ? (
          <P className="text-muted-foreground italic">No materialized datasets yet.</P>
        ) : (
          <ul className="space-y-0.5">
            {datasetsForMetric.map((d: any) => {
              const editorial = locationSets.find((ls) => ls.id === d.locationSet)
              const mapName =
                d.locationSetName ?? editorial?.shortName ?? d.locationSet
              return (
                <li key={d.slug}>
                  <DatasetRowLink
                    to={`/docs/datasets/${d.country.toLowerCase()}/${d.slug}`}
                    country={d.country}
                    mapName={mapName}
                    metricName={labelWithCadence(d.name, "")}
                    cadence={d.cadence}
                  />
                </li>
              )
            })}
          </ul>
        )}
      </Section>

      <RelatedMetrics related={related} hasAuthoredRelated={hasAuthoredRelated} />
    </div>
  )
}

/**
 * Countries where this metric actually has datasets. Concept pages are
 * cross-country, so without this a reader can't tell that e.g. Visitor
 * Profiles exist in the USA but not in Norway. Derived from the datasets
 * behind the metric, so it can never drift from what is actually available.
 */
function AvailableCountries({ datasets }: { datasets: any[] }) {
  const codes = Array.from(new Set(datasets.map((d: any) => d.country))).sort()
  if (codes.length === 0) return null
  return (
    <div className="mt-3 flex flex-wrap items-center gap-1.5">
      <span className="text-sm text-muted-foreground">Available in:</span>
      {codes.map((code) => {
        const p = COUNTRY_PRESENTATION[code]
        return (
          <RouterLink
            key={code}
            to={`/docs/datasets/${code.toLowerCase()}`}
            className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium text-muted-foreground transition-colors hover:border-primary hover:text-primary"
          >
            {p?.flag && <span aria-hidden>{p.flag}</span>}
            {p?.displayName ?? code}
          </RouterLink>
        )
      })}
    </div>
  )
}

/**
 * Cross-links to metric concepts that answer the neighbouring question — most
 * often "where do these visitors come from?" next to an audience metric.
 * Rendered only when the related concepts have datasets in this location type.
 */
function RelatedMetrics({
  related,
  hasAuthoredRelated,
}: {
  related: { name: string; to: string }[]
  hasAuthoredRelated?: boolean
}) {
  if (related.length === 0) return null
  return (
    <Section>
      {/* Some metrics carry a hand-written "Related metrics" section in their
          authored body. Don't print that heading twice — the automatic
          suggestions become a "See also" underneath it. */}
      <H2>{hasAuthoredRelated ? "See also" : "Related metrics"}</H2>
      <P className="text-sm text-muted-foreground">
        Metrics that are commonly read alongside this one.
      </P>
      <div className="flex flex-wrap gap-2">
        {related.map((r) => (
          <RouterLink
            key={r.to}
            to={r.to}
            className="inline-flex items-center rounded-full border px-3 py-1 text-sm text-muted-foreground transition-colors hover:border-primary hover:text-primary"
          >
            {r.name}
          </RouterLink>
        ))}
      </div>
    </Section>
  )
}

// Compact, fully clickable dataset row. Reads left-to-right as:
//   {ISO country} · {map} · {metric} · {cadence badge}
// Shared by both metric views (concept-backed and LTM-backed) so list rows
// look identical regardless of which detail page rendered them.
function DatasetRowLink({
  to,
  country,
  mapName,
  metricName,
  cadence,
}: {
  to: string
  country: string
  mapName: string
  metricName: string
  cadence: string
}) {
  return (
    <RouterLink
      to={to}
      className="group flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-muted transition-colors text-sm flex-wrap"
    >
      <DatabaseIcon className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
      <span className="font-mono text-xs uppercase text-muted-foreground">
        {country}
      </span>
      <span className="text-muted-foreground">·</span>
      <span className="text-muted-foreground">{mapName}</span>
      <span className="text-muted-foreground">·</span>
      <span className="font-medium text-foreground group-hover:text-primary transition-colors">
        {metricName}
      </span>
      <Badge variant="secondary" className="text-xs ml-auto">
        {cadence}
      </Badge>
    </RouterLink>
  )
}
